import { observable, ObservableMap, action, transaction } from 'mobx';
import Axios, { CancelTokenSource } from 'axios';

import { DutyItem, PersonModel, ApiClient, RosterType } from '@app/Models/WebApiModels';
import { DutyItemCache } from '@app/Models';
import { Range, RangeReadyStatus } from './Range';

import DateTimeService from '@app/Services/DateTimeService';
import Extensions from '@app/Services/Extensions';
import { applyDecorators } from '@app/Helpers/Decorator';
import { ApiHeaders, ApiHeadersKeys } from '@app/Services/ApiService';
import { appStore } from '@app/AppStore';

type PersonDayRange = {
    personId: string;
    dateFrom: Date;
    dateTo: Date;
    status: RangeReadyStatus;
};

type PersonMapItem = {
    ranges: PersonDayRange[];
};

type PersonModelDto = Omit<PersonModel, 'isActive'>;

export interface IBaseDutyStore {
    dutyList: Map<string, ObservableMap<number, DutyItemCache>>;
    getDutyStatus(personId: string, date: Date): RangeReadyStatus;
}

export interface IDutyStore extends IBaseDutyStore {
    loadViewport(persons: PersonModelDto[], viewportPersonRange: Range<number>, viewportDayRange: Range<Date>, totalDayRange: Range<Date>): void;
    loadDuties(dateRange: Range<Date>, personIds: string[]): PromiseLike<void>;
    setDuties(duties: DutyItem[]): void;
    clear(keepViewport: boolean): void;
    invalidate(personId: string, range: Range<Date>): void;
    setType(rosterType: string): void;
    cancel(): void;
}

export class DutyStore implements IDutyStore {
    @observable.shallow dutyList: Map<string, ObservableMap<number, DutyItem>> = new Map();
    private _cancellationSource: CancelTokenSource;
    private _apiClient: ApiClient = new ApiClient();
    private _rosterType: RosterType;
    private _packagePersons: number = 10;
    private _marginPerson: number = 2;

    private _maxDuties: number = 6 * 30 * 30 * 2; // 6 month to 30 persons twice, can be optimized
    private _dutyCounter: number = 0;

    @observable.shallow private _dutyMap: Map<string, PersonMapItem> = new Map();

    private _viewportDayFrom: Date = DateTimeService.today();
    private _viewportDayTo: Date = DateTimeService.today();
    private _viewportPersonIds: string[] = [];

    private _loadedRanges: PersonDayRange[] = [];

    constructor(rosterType: RosterType) {
        applyDecorators(this);
        this.setType(rosterType);
        this._cancellationSource = Axios.CancelToken.source();
    }

    public setType(rosterType: RosterType) {
        this._rosterType = rosterType;
    }

    public cancel() {
        this._cancellationSource?.cancel('Clear');
        this._cancellationSource = Axios.CancelToken.source();
    }

    @action
    public clear(keepViewport: true) {
        this.cancel();
        transaction(() => {
            !keepViewport ? this.dutyList.clear() : this._cleanUp(0);
            this._dutyMap.clear();
            this._loadedRanges.length = 0;
            this._dutyCounter = this._getDutyCount();
        });
    }

    private _getDutyCount() {
        let result = 0;
        this.dutyList.forEach((p) => {
            result += p.size;
        });
        return result;
    }

    //persons: full list of available persons
    //viewportPersonRange: actual persons to be displayed on the screen
    //viewportDayRange: actual days to be displayed on the screen
    //totalDayRange: all days available to scroll
    public loadViewport(persons: PersonModel[], viewportPersonRange: Range<number>, viewportDayRange: Range<Date>, totalDayRange: Range<Date>) {
        this._viewportDayFrom = viewportDayRange.from;
        this._viewportDayTo = viewportDayRange.to;
        this._viewportPersonIds = persons.slice(viewportPersonRange.from, viewportPersonRange.to + 1).map((x) => x.id);

        let personIndexFrom = viewportPersonRange.from;
        let personIndexTo = viewportPersonRange.to;

        const indexFromWithFullPrefetch = personIndexFrom - this._marginPerson < 0 ? 0 : personIndexFrom - this._marginPerson;
        const indexToWithFullPrefetch = personIndexTo + this._marginPerson > persons.length - 1 ? persons.length - 1 : personIndexTo + this._marginPerson;

        let dateFrom = viewportDayRange.from.getUTCDate() <= 15 ? DateTimeService.toFirstDayOfMonth(viewportDayRange.from) : DateTimeService.date(viewportDayRange.from.getUTCFullYear(), viewportDayRange.from.getUTCMonth(), 16);
        if (dateFrom < totalDayRange.from) {
            dateFrom = totalDayRange.from;
        }
        if (viewportDayRange.from <= dateFrom && dateFrom.getUTCFullYear() >= 2023 && dateFrom.getUTCMonth() + 1 === 1 && dateFrom.getUTCDate() === 1){
            //load till previous period start
            dateFrom = DateTimeService.date(dateFrom.getUTCFullYear() - 1, 11, 1 + 15);
        }

        let dateTo = viewportDayRange.to.getUTCDate() >= 16 ? DateTimeService.toLastDayOfMonth(viewportDayRange.to) : DateTimeService.date(viewportDayRange.to.getUTCFullYear(), viewportDayRange.to.getUTCMonth(), 15);
        if (dateTo > totalDayRange.to) {
            dateTo = totalDayRange.to;
        }
        if (dateTo.getUTCFullYear() >= 2022 && dateTo.getUTCMonth() + 1 === 12 && dateTo.getUTCDate() === 31){
            dateTo = DateTimeService.addDays(dateTo, 1);
        }

        if (dateFrom > dateTo) {
            throw new Error(`dateFrom ${dateFrom} can't be less than dateTo ${dateTo}`);
        }

        //split to ranges up to 1 month, december is till 1jan
        const dateRanges: Range<Date>[] = [];
        let currentDateFrom = dateFrom;
        while (currentDateFrom < dateTo) {
            let currentDateTo: Date;
            
            if (currentDateFrom.getUTCDate() < 15){
                currentDateTo = DateTimeService.addDays(currentDateFrom, 14);
            } else {
                currentDateTo = DateTimeService.toLastDayOfMonth(currentDateFrom);
            }

            if (currentDateTo > dateTo) {
                currentDateTo = dateTo;
            }

            let monthPeriodFrom = currentDateFrom;
            if (monthPeriodFrom.getUTCFullYear() >= 2023 && monthPeriodFrom.getUTCMonth() + 1 === 1 && monthPeriodFrom.getUTCDate() === 1){
                monthPeriodFrom = DateTimeService.addDays(monthPeriodFrom, 1);
            }
            let monthPeriodTo = currentDateTo;
            if (monthPeriodTo.getUTCFullYear() >= 2022 && monthPeriodTo.getUTCMonth() + 1 === 12 && monthPeriodTo.getUTCDate() === 31){
                monthPeriodTo = DateTimeService.addDays(monthPeriodTo, 1);
            }

            dateRanges.push({
                from: monthPeriodFrom,
                to: monthPeriodTo
            });

            currentDateFrom = DateTimeService.addDays(currentDateTo, 1);
        }

        for(let rangeIndex = 0 ; rangeIndex < dateRanges.length; rangeIndex++){
            const dateRange = dateRanges[rangeIndex];
            const nextRange = dateRanges[rangeIndex + 1];

            const personIds: string[] = [];
            const prefetchPersonIds: string[] = [];

            const canMergeWithNext = nextRange && nextRange.from.getUTCMonth() == dateRange.from.getUTCMonth();
            const mergedPersonIds: string[] = [];
            const mergedPrefetchPersonIds: string[] = [];
            const mergedRange: Range<Date> | null = nextRange ? { from: dateRange.from, to: nextRange.to } : null;

            for (let i = indexFromWithFullPrefetch; i <= indexToWithFullPrefetch; i++) {
                const person = persons[i];

                if (!this._isPersonRangeLoadingRequired(person.id, dateRange)) {
                    continue;
                }

                const shouldMerge = nextRange && canMergeWithNext && this._isPersonRangeLoadingRequired(person.id, nextRange);

                if (i < personIndexFrom || i > personIndexTo) {
                    shouldMerge ? mergedPrefetchPersonIds.push(person.id) : prefetchPersonIds.push(person.id);
                    continue;
                }

                shouldMerge ? mergedPersonIds.push(person.id) : personIds.push(person.id);
            }

            personIds.push(...prefetchPersonIds);
            while (personIds.length) {
                const personIdsToLoad = personIds.splice(0, this._packagePersons);
                void this.loadDuties(dateRange, personIdsToLoad);
            }
            
            mergedPersonIds.push(...mergedPrefetchPersonIds);
            while (mergedRange && mergedPersonIds.length) {
                const personIdsToLoad = mergedPersonIds.splice(0, this._packagePersons);
                void this.loadDuties(mergedRange, personIdsToLoad);
            }
        }
    }

    public async loadDuties(dateRange: Range<Date>, personIds: string[]) {
        const params = {
            rosterType: this._rosterType,
            dateFrom: dateRange.from,
            dateTo: dateRange.to,
            personIds: personIds.join(',')
        };

        const customApiHeader: ApiHeaders = {};
        if (appStore.jeppesenFileUri) {
            customApiHeader[ApiHeadersKeys.JeppesenFile] = appStore.jeppesenFileUri;
        }
        if (appStore.generatedPlanFileUri) {
            customApiHeader[ApiHeadersKeys.GeneratedPlanFile] = appStore.generatedPlanFileUri;
        }

        const personsDayRanges = this._setLoadingStatus(dateRange, personIds);
        try {
            const { data: dutyResponse } = await this._apiClient.dutyGet(params, { cancellationToken: this._cancellationSource.token, customApiHeader });
            this._resultToMap(dutyResponse, () => this._setReadyStatus(personsDayRanges, RangeReadyStatus.Ready));
        } catch (er) {
            if (Axios.isCancel(er)) {
                this._setReadyStatus(personsDayRanges, RangeReadyStatus.None);
                return;
            }
            this._setReadyStatus(personsDayRanges, RangeReadyStatus.Error);
            throw er;
        } finally {
            Extensions.executeTimeout(this._cleanUp, 500);
        }
    }

    public setDuties(duties: DutyItem[]) {
        transaction(() => {
            duties.forEach((dayData) => {
                const personDutyMap = this.dutyList.get(dayData.personId) || observable.map(new Map<number, DutyItem>(), { deep: false });
                const key = dayData.date.getTime();
                const duty = personDutyMap && personDutyMap.get(key);
                if (!duty || dayData.timestampDateTimeUtc >= duty.timestampDateTimeUtc) {
                    if (!duty) {
                        this._dutyCounter++;
                    }
                    personDutyMap.set(key, dayData);
                    this.dutyList.set(dayData.personId, personDutyMap);
                }
            });
        });
    }

    @action
    private _resultToMap(data: Array<DutyItem>, transactionAction?: Function) {
        transaction(() => {
            data.forEach((item: DutyItem) => {
                const personDutyMap = this.dutyList.get(item.personId) || observable.map(new Map<number, DutyItem>(), { deep: false });
                const isNew = !personDutyMap.size;
                if (!isNew) {
                    const hasDuty = personDutyMap.has(item.date.getTime());
                    if (!hasDuty) {
                        this._dutyCounter++;
                    }
                }
                personDutyMap.set(item.date.getTime(), item);
                if (isNew) {
                    this.dutyList.set(item.personId, personDutyMap);
                    this._dutyCounter++;
                }
            });
            if (transactionAction) transactionAction();
        });
    }

    private _setLoadingStatus(dateRange: Range<Date>, personIds: string[]) {
        const result: PersonDayRange[] = [];
        personIds.forEach((personId) => {
            let personMapItem = this._dutyMap.get(personId);
            if (!personMapItem) {
                personMapItem = {
                    ranges: observable.array([], { deep: false })
                };
                this._dutyMap.set(personId, personMapItem);
                personMapItem = this._dutyMap.get(personId) || personMapItem;
            }

            const range: PersonDayRange = {
                personId: personId,
                dateFrom: dateRange.from,
                dateTo: dateRange.to,
                status: RangeReadyStatus.Loading
            };

            personMapItem.ranges.push(range);
            const newItem = personMapItem.ranges[personMapItem.ranges.length - 1];

            this._loadedRanges.push(newItem);
            result.push(newItem);
        });

        return result;
    }

    private _setReadyStatus(ranges: PersonDayRange[], status: RangeReadyStatus) {
        ranges.forEach((range) => {
            const personMapItem = this._dutyMap.get(range.personId);
            if (!personMapItem) {
                return;
            }

            range.status = status;
            for (let i = personMapItem.ranges.length - 1; i >= 0; i--) {
                const currentRange = personMapItem.ranges[i];
                const isThisRange = currentRange === range;
                if (isThisRange || (currentRange.status !== RangeReadyStatus.Ready && currentRange.status !== RangeReadyStatus.Error)) {
                    continue;
                }
                if (range.dateTo >= currentRange.dateFrom && range.dateFrom <= currentRange.dateTo) {
                    if (range.status === currentRange.status) {
                        range.dateFrom = DateTimeService.min(range.dateFrom, currentRange.dateFrom);
                        range.dateTo = DateTimeService.max(range.dateTo, currentRange.dateTo);
                        personMapItem.ranges.splice(i, 1);
                        const index = this._loadedRanges.findIndex((x) => x.personId === currentRange.personId && DateTimeService.isSameDate(currentRange.dateTo, x.dateTo) && DateTimeService.isSameDate(currentRange.dateFrom, x.dateFrom));
                        if (index > -1) {
                            this._loadedRanges.splice(index, 1);
                        }
                        continue;
                    }

                    //cut existing range left
                    if (currentRange.dateTo > range.dateFrom) {
                        currentRange.dateTo = DateTimeService.addDays(range.dateFrom, -1);
                        const index = this._loadedRanges.findIndex((x) => x.personId === currentRange.personId && DateTimeService.isSameDate(currentRange.dateTo, x.dateTo) && DateTimeService.isSameDate(currentRange.dateFrom, x.dateFrom));
                        if (index !== -1) {
                            this._loadedRanges[index].dateTo = currentRange.dateTo;
                        }
                        if (currentRange.dateTo <= currentRange.dateFrom) {
                            personMapItem.ranges.splice(i, 1);
                            if (index > -1) {
                                this._loadedRanges.splice(index, 1);
                            }
                            continue;
                        }
                    }

                    //cut existing range right
                    if (currentRange.dateFrom < range.dateTo) {
                        currentRange.dateFrom = DateTimeService.addDays(range.dateTo, 1);
                        const index = this._loadedRanges.findIndex((x) => x.personId === currentRange.personId && DateTimeService.isSameDate(currentRange.dateTo, x.dateTo) && DateTimeService.isSameDate(currentRange.dateFrom, x.dateFrom));
                        if (index !== -1) {
                            this._loadedRanges[index].dateTo = currentRange.dateTo;
                        }
                        if (currentRange.dateTo <= currentRange.dateFrom) {
                            personMapItem.ranges.splice(i, 1);
                            if (index > -1) {
                                this._loadedRanges.splice(index, 1);
                            }
                            continue;
                        }
                    }
                }
            }
            this._dutyMap.set(range.personId, {...personMapItem});
        });
    }

    public getDutyStatus(personId: string, date: Date): RangeReadyStatus {
        const personMapItem = this._dutyMap.get(personId);
        if (!personMapItem) {
            return RangeReadyStatus.None;
        }

        let maxStatus = RangeReadyStatus.None;
        const ranges = personMapItem.ranges;
        for (let i = 0; i < ranges.length; i++) {
            const range = ranges[i];
            if (range.dateFrom <= date && date <= range.dateTo) {
                maxStatus = maxStatus < range.status ? range.status : maxStatus;
            }
        }

        return maxStatus;
    }

    private _isPersonRangeLoadingRequired(personId: string, range: Range<Date>) {
        const personMapItem = this._dutyMap.get(personId);
        if (!personMapItem) {
            return true;
        }

        const ranges = personMapItem.ranges;
        let isLoadingNeeded = true;
        let isFullyReady = true;
        let hasRanges = false;

        for (let i = 0; i < ranges.length; i++) {
            const statusRange = ranges[i];
            if (range.from >= statusRange.dateFrom && range.to <= statusRange.dateTo) {
                hasRanges = true;

                if (statusRange.status === RangeReadyStatus.Loading) {
                    isLoadingNeeded = false;
                    break;
                }

                if (statusRange.status !== RangeReadyStatus.Ready) {
                    isFullyReady = false;
                }
            }
        }

        if (hasRanges && isFullyReady) {
            return false;
        }

        return isLoadingNeeded;
    }

    @action
    public invalidate(personId: string, range: Range<Date>) {
        if (!this._viewportPersonIds.includes(personId) || range.to < this._viewportDayFrom || range.from > this._viewportDayTo) {
            let personMapItem = this._dutyMap.get(personId);
            if (!personMapItem) {
                personMapItem = {
                    ranges: observable.array([], { deep: false })
                };
            }

            for (let i = personMapItem.ranges.length - 1; i >= 0; i--) {
                const currentRange = personMapItem.ranges[i];

                if (range.to >= currentRange.dateFrom && range.to <= currentRange.dateTo) {
                    const part1 = { ...currentRange, dateTo: DateTimeService.addDays(range.from, -1) };
                    const part2 = { ...currentRange, dateFrom: range.from, dateTo: range.to, status: RangeReadyStatus.None };
                    const part3 = { ...currentRange, dateFrom: DateTimeService.addDays(range.to, 1) };
                    personMapItem.ranges.splice(i, 1, part1, part2, part3);

                    const index = this._loadedRanges.findIndex((x) => x.personId === currentRange.personId && DateTimeService.isSameDate(currentRange.dateTo, x.dateTo) && DateTimeService.isSameDate(currentRange.dateFrom, x.dateFrom));
                    if (index > -1) {
                        this._loadedRanges.splice(index, 1, part1, part2, part3);
                    }
                }
            }
            this._dutyMap.set(personId, { ...personMapItem });
        } else {
            void this.loadDuties(range, [personId]);
        }
    }

    @action.bound
    private _cleanUp(maxDuties?: number) {
        const maxRounds = this._loadedRanges.length;
        let round = 0;
        while (this._dutyCounter > (maxDuties || this._maxDuties)) {
            round++;
            if (round > maxRounds) return;
            const personMapItem = this._loadedRanges.shift();
            if (!personMapItem) return;

            if (personMapItem.status > 1) {
                if (!this._viewportPersonIds.includes(personMapItem.personId) || personMapItem.dateTo < this._viewportDayFrom || personMapItem.dateFrom > this._viewportDayTo) {
                    const personItem = this._dutyMap.get(personMapItem.personId);
                    const dutyMap = this.dutyList.get(personMapItem.personId);

                    if (!personItem || !dutyMap) {
                        continue;
                    }

                    const { dateFrom, dateTo, status, personId } = personMapItem;
                    const id = personItem.ranges.findIndex((el) => el.personId === personId && el.status === status && DateTimeService.isSameDate(el.dateFrom, dateFrom) && DateTimeService.isSameDate(el.dateTo, dateTo));
                    if (id > -1) {
                        personItem.ranges.splice(id, 1);
                    }

                    const dutyDates = DateTimeService.range(personMapItem.dateFrom, personMapItem.dateTo);
                    for (const date of dutyDates) {
                        const isDeleted = dutyMap.delete(date.getTime());
                        if (isDeleted) {
                            this._dutyCounter--;
                        }
                    }
                } else {
                    this._loadedRanges.push(personMapItem);
                }
            } else {
                this._loadedRanges.push(personMapItem);
            }
        }
    }
}
